<template>
  <div class="upload-panel">
    <div
      class="upload-dropzone"
      :class="{ 'upload-dropzone-active': dragActive }"
      @dragover.prevent="dragActive = true"
      @dragleave.prevent="dragActive = false"
      @drop.prevent="handleDrop"
      @click="$refs.fileInput.click()">
      <i class="fa fa-cloud-upload upload-dropzone-icon"></i>
      <p class="upload-dropzone-text">Drag and drop your CSV here, or click to browse</p>
      <p class="upload-dropzone-filename" v-if="fileName">{{ fileName }}</p>
      <input ref="fileInput" type="file" accept=".csv" class="upload-file-input" @change="handleFileInput" />
    </div>

    <div class="upload-template-row">
      <a href="#" @click.prevent="downloadTemplate">
        <i class="fa fa-download"></i> Download CSV template
      </a>
      <span class="upload-template-note">Template column: <code>account_id</code> (one per row).</span>
    </div>

    <div class="upload-paste">
      <label class="upload-paste-label">Or paste account IDs <span class="upload-template-note">(one per line, or comma/space separated)</span></label>
      <textarea
        class="form-control"
        rows="4"
        :placeholder="pastePlaceholder"
        v-model="pastedIds"
        @input="handlePastedIds"></textarea>
    </div>
    <span class="not-enforced-note">
      The richer PRD template (phone_number / name / market / language / journey_stage) needs a phone-to-account
      resolution step that doesn't exist in this system yet — pending confirmation with engineering. Uploading by
      <code>account_id</code> works end-to-end today against the same segment save/count contract as filter-built segments.
    </span>

    <div v-if="rows.length" class="upload-summary">
      <span class="upload-summary-stat"><strong>{{ totalRows }}</strong> total rows</span>
      <span class="upload-summary-stat upload-summary-valid"><strong>{{ validAccountIds.length }}</strong> valid</span>
      <span class="upload-summary-stat upload-summary-error">
        <strong>{{ invalidRows.length }}</strong> error{{ invalidRows.length === 1 ? '' : 's' }}
        <a href="#" v-if="invalidRows.length" @click.prevent="showErrors = !showErrors">{{ showErrors ? 'hide' : 'view' }}</a>
      </span>
      <span class="upload-summary-stat">STOP exclusions applied automatically when the audience count is calculated.</span>
    </div>

    <ul v-if="showErrors" class="upload-error-list">
      <li v-for="err in invalidRows" :key="err.row">Row {{ err.row }}: {{ err.reason }}</li>
    </ul>

    <div v-if="previewRows.length" class="upload-preview">
      <label>Preview (first 5 rows)</label>
      <table class="table table-bordered table-condensed">
        <thead>
          <tr><th v-for="col in previewColumns" :key="col">{{ col }}</th></tr>
        </thead>
        <tbody>
          <tr v-for="(row, index) in previewRows" :key="index">
            <td v-for="col in previewColumns" :key="col">{{ row[col] }}</td>
          </tr>
        </tbody>
      </table>
    </div>

    <div class="row">
      <div class="form-group col-xs-6 col-sm-6 col-md-4">
        <label class="required">Country</label>
        <treeselect
          :clearable="false"
          placeholder="--Choose a Country--"
          :options="countryOptions"
          v-model="countryName"
          @input="handleCountryInput" />
        <span v-if="submitted && !countryId" class="text-red">Please choose a country.</span>
      </div>
      <div class="form-group col-xs-6 col-sm-6 col-md-4">
        <label class="required">Segment Name</label>
        <input type="text" class="form-control" v-model="segmentName" @input="nameManuallyEdited = true" placeholder="Segment name" />
        <span v-if="submitted && !segmentName.trim()" class="text-red">Please enter a segment name.</span>
      </div>
    </div>

    <div class="action-button">
      <button type="button" class="btn btn-primary" :disabled="!validAccountIds.length" @click="getAudience">Get Audience</button>
      &nbsp; &nbsp;
      <button type="button" class="btn btn-primary" :disabled="!validAccountIds.length" @click="saveSegment">Save Segment</button>
    </div>

    <BlockUI v-if="loading"><i class="fa fa-cog fa-spin fa-3x fa-fw"></i><br/>Please wait...</BlockUI>
    <AudienceTable :audienceData="audienceData" :audienceCount="audienceCount" :showTable="true" />
  </div>
</template>

<script>
import Papa from 'papaparse'
import moment from 'moment'
import Treeselect from '@riophae/vue-treeselect'
import '@riophae/vue-treeselect/dist/vue-treeselect.css'
import api from '../../../../utils/latestApi'
import apiold from '../../../../utils/api'
import { getEnv, downloadCSV } from '../../../../utils/utilities'
import AudienceTable from '../table'
import { generateSegmentName } from './helpers'

export default {
  name: 'upload-audience-panel',
  components: {
    AudienceTable,
    treeselect: Treeselect
  },
  data () {
    return {
      loading: false,
      submitted: false,
      env: '',
      dragActive: false,
      fileName: '',
      pastePlaceholder: '919999999999-India\n918888888888\n917777777777-India',
      pastedIds: '',
      rows: [],
      invalidRows: [],
      showErrors: false,
      countryIdMap: {},
      countryOptions: [],
      countryId: null,
      countryName: null,
      segmentName: '',
      nameManuallyEdited: false,
      audienceCount: null,
      audienceData: []
    }
  },
  computed: {
    totalRows () {
      return this.rows.length
    },
    validAccountIds () {
      return [...new Set(
        this.rows
          .map(row => (row.account_id || '').toString().trim())
          .filter(Boolean)
      )]
    },
    previewRows () {
      return this.rows.slice(0, 5)
    },
    previewColumns () {
      return this.previewRows.length ? Object.keys(this.previewRows[0]) : []
    }
  },
  async mounted () {
    this.env = await getEnv()
    await this.fetchCountryIdMap()
    await this.fetchCountryOptions()
  },
  watch: {
    // A count only describes the IDs/country it was run for; drop it as soon as either changes.
    validAccountIds () { this.audienceCount = null; this.audienceData = [] },
    countryId () { this.audienceCount = null; this.audienceData = [] }
  },
  methods: {
    async fetchCountryIdMap () {
      try {
        const response = await apiold.buildRequest('/getCountryList', 'POST')
        const map = {}
        ;(response.data.payload || []).forEach(item => { map[item.country_name] = item.country_id })
        this.countryIdMap = map
      } catch (error) {
        this.$swal({ type: 'error', text: 'Failed to load country list' })
      }
    },
    async fetchCountryOptions () {
      try {
        const response = await apiold.buildRequest('/audience-builder/journey-map/countries', 'GET')
        this.countryOptions = (response.data.payload || []).map(name => ({ id: name, label: name }))
      } catch (error) {
        this.$swal({ type: 'error', text: 'Failed to load countries' })
      }
    },
    handleCountryInput (countryName) {
      this.countryId = this.countryIdMap[countryName] || null
    },
    handleDrop (e) {
      this.dragActive = false
      const file = e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files[0]
      if (file) this.parseFile(file)
    },
    handleFileInput (e) {
      const file = e.target.files && e.target.files[0]
      if (file) this.parseFile(file)
    },
    parseFile (file) {
      this.pastedIds = ''
      this.fileName = file.name
      Papa.parse(file, {
        header: true,
        skipEmptyLines: true,
        transformHeader: h => h.trim().toLowerCase(),
        complete: (results) => this.handleParsed(results.data || [])
      })
    },
    handleParsed (data) {
      this.rows = data
      this.showErrors = false
      this.invalidRows = data
        .map((row, index) => ({ row: index + 2, reason: !((row.account_id || '').toString().trim()) ? 'Missing account_id' : null }))
        .filter(err => err.reason)
      this.maybeGenerateName()
    },
    // Pasted account IDs feed the exact same { account_id } row structure the CSV path produces,
    // so validAccountIds / summary / count / save all work unchanged. Split on newlines, commas,
    // semicolons or whitespace.
    handlePastedIds () {
      const ids = (this.pastedIds || '').split(/[\s,;]+/).map(id => id.trim()).filter(Boolean)
      if (ids.length) this.fileName = ''
      this.rows = ids.map(id => ({ account_id: id }))
      this.showErrors = false
      this.invalidRows = []
    },
    downloadTemplate () {
      downloadCSV({ fields: ['account_id'], data: [['ACC-00001'], ['ACC-00002']] }, 'audience_upload_template')
    },
    maybeGenerateName () {
      if (this.nameManuallyEdited || !this.fileName) return
      const base = this.fileName.replace(/\.csv$/i, '')
      this.segmentName = generateSegmentName(base, 'upload', [])
    },
    formatAudienceRawData (rawData) {
      return (rawData || []).map((ele, index) => ({
        key: index,
        accountId: ele.accountId,
        country: ele.country || '-',
        channel: ele.usertype || '-',
        gender: ele.gender || '-',
        age: ele.age || '-',
        language: ele.language || '-',
        sessionId: ele.sessionId,
        keyword: ele.keyword,
        conversation: ele.conversation,
        stage: ele.stage,
        answer: ele.answer,
        queText: ele.queText,
        createdDate: ele.engagementCreatedAt ? moment(ele.engagementCreatedAt).utc().format('DD-MMM-YYYY, hh:mm A') : '-'
      }))
    },
    async getAudience () {
      this.submitted = true
      if (!this.countryId || !this.validAccountIds.length) return
      this.loading = true
      const payload = {
        queryName: this.segmentName,
        countryId: this.countryId,
        country_name: this.countryName,
        needSampleData: true,
        numberOfTranscripts: 0,
        criteria: { account_ids: this.validAccountIds },
        include_tnc_false_users: false
      }
      try {
        const response = await api.post(`audience-builder/${this.env.env}/users/re-engagement-audience`, payload, { baseURL: this.env.baseURL })
        this.audienceCount = Number(response.data.totalCounts) || 0
        this.audienceData = this.formatAudienceRawData(response.data.data)
      } catch (error) {
        this.$swal({ type: 'error', text: error.response ? error.response.data.message : 'An error occurred' })
      } finally {
        this.loading = false
      }
    },
    async saveSegment () {
      this.submitted = true
      if (!this.countryId || !this.segmentName.trim() || !this.validAccountIds.length) return

      this.loading = true
      const criteria = {
        account_ids: this.validAccountIds,
        // Only store a count that was actually computed for these IDs (null = never counted).
        ...(this.audienceCount !== null ? { savedUserCount: this.audienceCount } : {})
      }
      const params = {
        id: null,
        query_name: this.segmentName.trim(),
        country_id: this.countryId,
        country_name: this.countryName,
        criteria,
        query_type: 'Custom'
      }
      try {
        await api.post(`audience-builder/${this.env.env}/queries/save`, params, { baseURL: this.env.baseURL })
        this.$swal({ type: 'success', text: 'Segment saved successfully' })
        this.$emit('segment-saved')
      } catch (error) {
        if (error.response && error.response.status === 409) {
          this.$swal({ type: 'error', text: 'A segment with the same name already exists' })
        } else {
          this.$swal({ type: 'error', text: error.response && error.response.data ? error.response.data.message : 'An error occurred' })
        }
      } finally {
        this.loading = false
      }
    }
  }
}
</script>

<style scoped>
.upload-panel {
  margin-bottom: 20px;
}
.upload-dropzone {
  position: relative;
  border: 2px dashed #cbd5e0;
  border-radius: 10px;
  padding: 32px 16px;
  text-align: center;
  background: #f9fafb;
  cursor: pointer;
  transition: border-color 0.15s ease, background 0.15s ease;
}
.upload-dropzone:hover,
.upload-dropzone-active {
  border-color: #0D9488;
  background: #E6F5F4;
}
.upload-dropzone-icon {
  font-size: 32px;
  color: #a0aec0;
  margin-bottom: 8px;
  display: block;
}
.upload-dropzone-text {
  margin: 0;
  color: #4a5568;
}
.upload-dropzone-filename {
  margin: 8px 0 0;
  font-weight: 600;
  color: #2d3748;
}
.upload-file-input {
  display: none;
}
.upload-template-row {
  margin-top: 10px;
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}
.upload-template-note {
  color: #999;
  font-size: 12px;
}
.upload-paste {
  margin-top: 12px;
}
.upload-paste-label {
  display: block;
  margin-bottom: 4px;
  font-weight: 600;
  color: #4a5568;
}
.upload-summary {
  display: flex;
  gap: 18px;
  flex-wrap: wrap;
  margin-top: 16px;
  padding: 10px 14px;
  background: #f7fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  font-size: 13px;
}
.upload-summary-valid strong {
  color: #276749;
}
.upload-summary-error strong {
  color: #c53030;
}
.upload-error-list {
  margin-top: 8px;
  color: #c53030;
  font-size: 12px;
  max-height: 150px;
  overflow-y: auto;
}
.upload-preview {
  margin-top: 16px;
}
.not-enforced-note {
  display: block;
  color: #975a16;
  background: #fffaf0;
  border: 1px solid #feebc8;
  border-radius: 4px;
  padding: 4px 8px;
  font-size: 12px;
  margin-top: 6px;
}
.required:after {
  content: " *";
  color: red;
}
</style>



// WEBPACK FOOTER //
// UploadAudiencePanel.vue?01fc70b2